import * as React from "react";
import styled from "styled-components";
import { useValidator, validPattern } from "../../../utils/validators";
import { lang } from "../../../constants";

const Container = styled.div`
  display: flex;
  align-items: center;
`;

const Label = styled.div<{ 
  color?: string; 
  fontSize?: number; 
  fontWeight?: number; 
  isUppercase?: boolean; 
}>`
  color: ${props => props.color || '#333'};
  font-size: ${props => props.fontSize ? `${props.fontSize}px` : '16px'};
  font-weight: ${props => props.fontWeight || 400};
  text-transform: ${props => props.isUppercase ? 'uppercase' : 'none'};
  margin-bottom: 8px;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledInput = styled.input.withConfig({
  shouldForwardProp: (prop) => !['bgColor', 'borderColor', 'borderRadius', 'fontWeight', 'fontSize', 'color', 'width', 'height', 'padding', 'labelColor', 'labelFontSize', 'labelFontWeight'].includes(prop as string),
})<{
  bgColor?: string;
  borderColor?: string;
  borderRadius?: number;
  fontWeight?: number;
  fontSize?: number;
  color?: string;
  width?: number;
  height?: number;
  padding?: string;
  labelColor?: string;
  labelFontSize?: number;
  labelFontWeight?: number;
}>`
  background-color: ${props => props.bgColor || 'transparent'};
  border: 1px solid ${props => props.borderColor || '#ccc'};
  border-radius: ${props => props.borderRadius ? `${props.borderRadius}px` : '4px'};
  font-weight: ${props => props.fontWeight || 400};
  font-size: ${props => props.fontSize ? `${props.fontSize}px` : '14px'};
  color: ${props => props.color || '#333'};
  width: ${props => props.width ? `${props.width}px` : '100%'};
  height: ${props => props.height ? `${props.height}px` : 'auto'};
  padding: ${props => props.padding || '8px 12px'};

  &::placeholder {
    font-size: ${props => props.labelFontSize ? `${props.labelFontSize}px` : '16px'};
    color: ${props => props.labelColor || '#B0B0B0'};
    font-weight: ${props => props.labelFontWeight || 400};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.borderColor || '#e39435'};
    box-shadow: 0 0 0 2px rgba(227, 148, 53, 0.2);
  }
`;

const AdornmentSpan = styled.span<{
  fontSize?: number;
  fontWeight?: number;
  color?: string;
  marginRight?: string;
  marginLeft?: string;
}>`
  font-size: ${props => props.fontSize ? `${props.fontSize}px` : '14px'};
  font-weight: ${props => props.fontWeight || 400};
  color: ${props => props.color || '#333'};
  margin-right: ${props => props.marginRight};
  margin-left: ${props => props.marginLeft};
`;

const ErrorMessage = styled.div`
  color: #ff4444;
  height: 24px;
  font-size: 14px;
  margin-top: 4px;
`;

const EmptySpace = styled.div`
  height: 8px;
`;

interface Iprops {
  titleEN: string;
  titleCN: string;
  type?: string;
  value: any;
  callback: Function;
  disabled?: boolean | false;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  isRequired?: boolean;
  isNumberOnly?: boolean;
  isTextOnly?: boolean;
  patternType: any;
  width?: number;
  height?: number;
  padding?: string;
  borderColor?: string;
  borderRadius?: number;
  fontWeight?: number;
  fontSize?: number;
  color?: string;
  bgColor?: string;
  is320?: boolean;
  noPlaceholder?: boolean;
  placeholder?: string;
  startAdortment?: string;
  symbolFontWeight?: number;
  symbolFontSize?: number;
  symbolColor?: string;
  adortmentPosition?: string;
  label?: string;
  labelColor?: string;
  labelFontSize?: number;
  labelFontWeight?: number;
  isUppercase?: boolean;
  decimalPlace?: number;
  integerPlace?: number;
}

export default function CustomTextField(props: Iprops) {
  const validators = {
    field: (value: string) => check(value, props.minLength, props.maxLength),
  };

  const initialValues = {
    field: {
      value: props.value || (props.patternType === "number" ? 0 : ""),
      accTxtBoxisValid: true,
      isTouched: false,
    },
  };

  const { values, errors, handleChange, setValues } = useValidator(
    initialValues,
    validators
  );

  // Sync internal state with props.value changes
  React.useEffect(() => {
    setValues(prev => ({
      ...prev,
      field: {
        ...prev.field,
        value: props.value || (props.patternType === "number" ? 0 : ""),
      }
    }));
  }, [props.value, props.patternType, setValues]);

  // Custom handleChange function to handle decimal place validation and special character filtering
  const customHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let { value } = event.target;
    
    // Apply special character filtering for text inputs (same regex as AddNewBundleDeal)
    if (props.patternType === "text") {
      value = value.replace(/[^a-zA-Z0-9\s\-'.,()&]/g, '');
    }
    
    // If decimalPlace is specified and this is a number input, validate decimal places
    if (props.decimalPlace !== undefined && props.patternType === "number") {
      // Check if the input has more decimal places than allowed
      if (value.includes('.')) {
        const decimalPart = value.split('.')[1];
        if (decimalPart && decimalPart.length > props.decimalPlace) {
          // Prevent the input by not calling the original handleChange
          return;
        }
      }
      
      // Additional regex validation for decimal places
      const decimalRegex = new RegExp(`^\\d*\\.?\\d{0,${props.decimalPlace}}$`);
      if (!decimalRegex.test(value) && value !== '') {
        return; // Prevent invalid input
      }
    }
    
    // Create a synthetic event with the filtered value
    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        value: value
      }
    };
    
    // Call the original handleChange with filtered value
    handleChange(syntheticEvent as React.ChangeEvent<HTMLInputElement>);
    
    // Always call the callback with the filtered value to ensure parent state updates
    props.callback(value);
  };

  const check = (value: any, minLength: number = 0, maxLength: number = 0) => {
    switch (props.patternType) {
      case "email": {
        return checkEmail(value);
      }
      case "postalCode": {
        return checkPostalCode(value);
      }
      case "mobile": {
        return checkMobile(value);
      }
      case "text": {
        return checkText(value, minLength, maxLength);
      }
      case "number": {
        return !isNaN(value) && value >= (props.min || 0);
      }
    }
  };

  const checkEmail = (value: any) => {
    return validPattern({
      paramPattern: value,
      patternType: "email",
    });
  };

  const checkMobile = (value: any) => {
    return validPattern({
      paramPattern: value,
      patternType: "mobile",
    });
  };

  const checkPostalCode = (value: any) => {
    return validPattern({
      paramPattern: value,
      patternType: "postalCode",
    });
  };

  const checkText = (value: any, minLength: number, maxLength: number) => {
    if (minLength !== 0 && maxLength !== 0) {
      if (props.isNumberOnly) {
        if (props.isRequired) {
          return (
            !isNaN(value) &&
            value.length > 0 &&
            value.length >= minLength &&
            value.length <= maxLength
          );
        } else return true;
      } else if (props.isTextOnly) {
        return (
          /^[a-z]+$/i.test(value) &&
          value.length > 0 &&
          value.length >= minLength &&
          value.length <= maxLength
        );
      } else {
        return (
          value.length > 0 &&
          value.length >= minLength &&
          value.length <= maxLength
        );
      }
    } else if (minLength === 0 && maxLength > 0) {
      if (props.isNumberOnly) {
        if (props.isRequired) {
          return !isNaN(value) && value.length > 0 && value.length <= maxLength;
        } else return true;
      } else if (props.isTextOnly) {
        return (
          /^[a-z]+$/i.test(value) &&
          value.length > 0 &&
          value.length >= minLength &&
          value.length <= maxLength
        );
      } else {
        return (
          value.length > 0 &&
          value.length >= minLength &&
          value.length <= maxLength
        );
      }
    } else if (maxLength === 0 && minLength > 0) {
      if (props.isNumberOnly) {
        if (props.isRequired) {
          return !isNaN(value) && value.length > 0 && value.length >= minLength;
        } else return true;
      } else if (props.isTextOnly) {
        return (
          /^[a-z]+$/i.test(value) &&
          value.length > 0 &&
          value.length >= minLength &&
          value.length <= maxLength
        );
      } else {
        return (
          value.length > 0 &&
          value.length >= minLength &&
          value.length <= maxLength
        );
      }
    } else {
      if (props.isNumberOnly) {
        if (props.isRequired) {
          return !isNaN(value) && value.length > 0;
        } else return true;
      } else {
        return (
          (props.isRequired ? value.length > 0 : true) &&
          (minLength !== 0 ? value.length >= minLength : true) &&
          (maxLength !== 0 ? value.length <= maxLength : true)
        );
      }
    }
  };

  const keyUp = (event: any) => {
    props.callback(event.target.value);
  };

  return (
    <>
      <Container>
        {props.label && (
          <Label
            color={props.labelColor}
            fontSize={props.labelFontSize}
            fontWeight={props.labelFontWeight}
            isUppercase={props.isUppercase}
          >
            {props.label}
          </Label>
        )}
        <InputWrapper>
          {props.startAdortment && !props.adortmentPosition && (
            <AdornmentSpan
              fontSize={props.symbolFontSize}
              fontWeight={props.symbolFontWeight}
              color={props.symbolColor}
              marginRight="8px"
            >
              {props.startAdortment}
            </AdornmentSpan>
          )}

          <StyledInput
            className={"cus_input_auto"}
            type={props.type || "text"}
            min={props.min || 0}
            max={props.max}
            value={values.field.value}
            name="field"
            onChange={customHandleChange}
            onKeyUp={keyUp}
            disabled={props.disabled}
            placeholder={
              props.noPlaceholder
                ? ""
                : props.placeholder || (lang() === "en"
                ? props.titleEN
                : props.titleCN)
            }
            bgColor={props.bgColor}
            borderColor={props.borderColor}
            borderRadius={props.borderRadius}
            fontWeight={props.fontWeight}
            fontSize={props.fontSize}
            color={props.color}
            width={props.width}
            height={props.height}
            padding={props.padding}
            labelColor={props.labelColor}
            labelFontSize={props.labelFontSize}
            labelFontWeight={props.labelFontWeight}
          />
          
          {props.startAdortment &&
            props.adortmentPosition &&
            props.adortmentPosition === "right" && (
              <AdornmentSpan
                fontSize={props.symbolFontSize}
                fontWeight={props.symbolFontWeight}
                color={props.symbolColor}
                marginLeft="8px"
              >
                {props.startAdortment}
              </AdornmentSpan>
            )}
        </InputWrapper>
      </Container>

      {errors.field && values.field.isTouched ? (
        <ErrorMessage>
          {lang() === "en"
            ? "Please enter a valid " + props.titleEN + "."
            : "请输入有效的" + props.titleCN + "。"}
        </ErrorMessage>
      ) : (
        <EmptySpace />
      )}
    </>
  );
}
